import { SwaggerOperation } from '@activepieces/shared';
import { t } from 'i18next';
import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/search-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const UNTAGGED_GROUP = '__untagged__';

const METHOD_STYLES: Record<string, string> = {
  get: 'bg-sky-100 text-sky-700 border-sky-200',
  post: 'bg-green-100 text-green-700 border-green-200',
  put: 'bg-amber-100 text-amber-700 border-amber-200',
  patch: 'bg-amber-100 text-amber-700 border-amber-200',
  delete: 'bg-red-100 text-red-700 border-red-200',
};

type SwaggerRoutesPickerProps = {
  operations: SwaggerOperation[];
  /** Action names currently ticked. Owned by the parent so it can be saved. */
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
};

type OperationGroup = {
  tag: string;
  label: string;
  operations: SwaggerOperation[];
};

const groupByTag = (operations: SwaggerOperation[]): OperationGroup[] => {
  const groups = new Map<string, SwaggerOperation[]>();
  for (const operation of operations) {
    const tag = operation.tags[0] ?? UNTAGGED_GROUP;
    groups.set(tag, [...(groups.get(tag) ?? []), operation]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tag, groupOperations]) => ({
      tag,
      label: tag === UNTAGGED_GROUP ? t('Other') : tag,
      operations: groupOperations,
    }));
};

/**
 * Tri-state of a group's checkbox: none of its routes ticked, all of them, or
 * anything in between.
 */
const groupCheckboxState = (
  selectedCount: number,
  total: number,
): boolean | 'indeterminate' => {
  if (selectedCount === 0) {
    return false;
  }
  return selectedCount === total ? true : 'indeterminate';
};

const MethodBadge = ({ method }: { method: string }) => (
  <Badge
    variant="outline"
    className={cn(
      'w-16 justify-center font-mono text-[10px] uppercase',
      METHOD_STYLES[method.toLowerCase()] ??
        'bg-muted text-muted-foreground border-border',
    )}
  >
    {method}
  </Badge>
);

/**
 * Route selection for a Swagger definition. Every route is ticked when a
 * document is imported; unticking one hides it from the client's step selector
 * and makes it unreachable at execution time, so a route already used by a flow
 * should be unticked knowingly.
 */
const SwaggerRoutesPicker = ({
  operations,
  selected,
  onChange,
}: SwaggerRoutesPickerProps) => {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query.length === 0) {
      return operations;
    }
    return operations.filter(
      (operation) =>
        operation.path.toLowerCase().includes(query) ||
        operation.method.toLowerCase().includes(query) ||
        operation.displayName.toLowerCase().includes(query),
    );
  }, [operations, search]);

  const groups = useMemo(() => groupByTag(visible), [visible]);

  const isSearching = search.trim().length > 0;
  const accordionValue = isSearching
    ? groups.map((group) => group.tag)
    : openGroups;

  const toggle = (actionName: string) => {
    const next = new Set(selected);
    if (next.has(actionName)) {
      next.delete(actionName);
    } else {
      next.add(actionName);
    }
    onChange(next);
  };

  const setGroupSelection = (
    groupOperations: SwaggerOperation[],
    enabled: boolean,
  ) => {
    const next = new Set(selected);
    for (const operation of groupOperations) {
      if (enabled) {
        next.add(operation.actionName);
      } else {
        next.delete(operation.actionName);
      }
    }
    onChange(next);
  };

  const setAllVisible = (enabled: boolean) => {
    setGroupSelection(visible, enabled);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <SearchInput
          placeholder={t('Filter routes')}
          value={search}
          onChange={setSearch}
          showDeselect={search.length > 0}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAllVisible(true)}
          disabled={visible.length === 0}
        >
          {t('Select All')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAllVisible(false)}
          disabled={visible.length === 0}
        >
          {t('Deselect All')}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {t('{selected} of {total} routes exposed to the client', {
            selected: selected.size,
            total: operations.length,
          })}
        </p>
        <Badge
          variant="secondary"
          className="shrink-0 font-mono text-[11px] font-normal"
        >
          {selected.size}/{operations.length}
        </Badge>
      </div>

      {visible.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {t('No results.')}
        </p>
      ) : (
        <Accordion
          type="multiple"
          value={accordionValue}
          onValueChange={setOpenGroups}
          className="overflow-hidden rounded-lg border shadow-sm"
        >
          {groups.map((group) => {
            const groupSelectedCount = group.operations.filter((operation) =>
              selected.has(operation.actionName),
            ).length;
            const groupState = groupCheckboxState(
              groupSelectedCount,
              group.operations.length,
            );

            return (
              <AccordionItem
                key={group.tag}
                value={group.tag}
                className="border-b last:border-b-0"
              >
                <div className="flex items-center gap-3 bg-muted/40 pl-3 pr-3 transition-colors duration-150 hover:bg-muted/60">
                  <Checkbox
                    checked={groupState}
                    onCheckedChange={(checked) =>
                      setGroupSelection(group.operations, checked === true)
                    }
                    aria-label={t('Toggle all routes in {group}', {
                      group: group.label,
                    })}
                  />
                  <AccordionTrigger
                    headerClassName="flex-1"
                    className="gap-3 py-2.5 hover:no-underline"
                  >
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {group.label}
                      </span>
                      <Badge
                        variant="secondary"
                        className="shrink-0 font-mono text-[10px] font-normal"
                      >
                        {groupSelectedCount}/{group.operations.length}
                      </Badge>
                    </span>
                  </AccordionTrigger>
                </div>
                <AccordionContent className="pb-0 pt-0">
                  <div className="divide-y">
                    {group.operations.map((operation) => (
                      <label
                        key={operation.actionName}
                        className="flex cursor-pointer items-center gap-3 py-2 pl-8 pr-3 transition-colors hover:bg-accent/40"
                      >
                        <Checkbox
                          checked={selected.has(operation.actionName)}
                          onCheckedChange={() => toggle(operation.actionName)}
                          // A wrapping `<label>` names a form control, not a
                          // `role="checkbox"` button — without this the route
                          // is announced as an unnamed checkbox.
                          aria-label={t('Expose {method} {path}', {
                            method: operation.method.toUpperCase(),
                            path: operation.path,
                          })}
                        />
                        <MethodBadge method={operation.method} />
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-mono text-xs">
                            {operation.path}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {operation.displayName}
                          </span>
                        </span>
                        <span className="ml-auto flex items-center gap-2">
                          {operation.deprecated && (
                            <Badge variant="secondary">{t('Deprecated')}</Badge>
                          )}
                          {!operation.present && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertTriangle
                                  className="h-4 w-4 text-amber-600"
                                  role="img"
                                  aria-label={t(
                                    'Route missing from the source document',
                                  )}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                {t(
                                  'This route no longer exists in the source document. Flows still referencing it will fail.',
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

export { SwaggerRoutesPicker };
